import { isBrowser } from '@/app/utils/browser';
import { CUSTOM_EVENT_DYNAMIC_EVENT } from '@/tag-manager/constants';
import type { FormSubmissionData } from '@/types/tracking/tag-manager';

export const GTM_ID = process?.env?.NEXT_PUBLIC_GTM_ID || '';

export const isDebug = (asPath: string) => {
  return asPath.includes('gtm_debug');
};

export function isDataLayerAvailable() {
  return isBrowser && window.dataLayer;
}

interface ILeadInterest {
  district?: string;
  city?: string;
  dealerName: string;
  id: string;
  content_ids: string;
  model?: string;
  leadId?: string;
}
export function formatModelForFacebookTracking(value: string) {
  switch (value) {
    case 'pickup':
      return 'รถปิกอัพ';
    case 'ppv':
      return 'รถอเนกประสงค์';
  }
  return 'รถบรรทุก';
}
export function trackFBLeadInterest(params: ILeadInterest) {
  if (!isDataLayerAvailable()) {
    return;
  }

  window.dataLayer.push({
    event: 'submit-crp-register-interest',
    ...params,
  });
}

export function trackFBLeadFinancial(params: ILeadInterest) {
  if (!isDataLayerAvailable()) {
    return;
  }
  window.dataLayer.push({
    event: 'submit-crp-register',
    ...params,
  });
}
interface ILeadFinancialInitiated {
  content_ids: string;
}
export function trackFBLeadFinancialInitiated(params: ILeadFinancialInitiated) {
  if (!isDataLayerAvailable()) {
    return;
  }
  window.dataLayer.push({
    event: 'submit-crp-register-initiated',
    ...params,
  });
}

interface ILeadInterestInitiated {
  content_ids: string;
}
export function trackFBLeadInterestInitiated(params: ILeadInterestInitiated) {
  if (!isDataLayerAvailable()) {
    return;
  }
  window.dataLayer.push({
    event: 'submit-crp-register-interest-initiated',
    ...params,
  });
}

export function trackFBDealerSearch(params: {
  city: string;
  district: string;
}) {
  if (!isDataLayerAvailable()) {
    return;
  }

  window.dataLayer.push({
    event: 'dealer-submit',
    ...params,
  });
}

interface IProductListFilter {
  modelClass?: string;
  modelSubCategory?: string;
  businessType?: string;
  segmentType?: string;
  bodyType?: string;
}

export function trackProductListFilter(params: IProductListFilter) {
  if (!isDataLayerAvailable()) {
    return;
  }

  window.dataLayer.push({ event: 'product-list-filter', ...params });
}

interface LCVCalculatorTracking {
  id: string;
  type?: string;
  drivetrain?: string;
  color?: string;
  preferred_price_range?: string;
  interest_rate?: string;
  period?: string;
}
export function trackLCVCalculator(params: LCVCalculatorTracking) {
  if (!isDataLayerAvailable()) {
    return;
  }

  window.dataLayer.push({
    event: 'calculator-submit',
    content_ids: params.id,
    content_type: params.type,
    drivetrain: params.drivetrain,
    color: params.color,
    preferred_price_range: params.preferred_price_range,
    interest_rate: params.interest_rate,
    period: params.period,
  });
}

interface IFormSubmission {
  formId: string;
  formName: string;
  section?: string;
}

export function trackFormInitiated(params: IFormSubmission) {
  if (!isDataLayerAvailable()) {
    return;
  }
  window.dataLayer.push({
    event: 'form-initiated',
    ...params,
  });
}

export function trackFormSuccess(params: IFormSubmission) {
  if (!isDataLayerAvailable()) {
    return;
  }
  window.dataLayer.push({
    event: 'form-success',
    ...params,
  });
}

interface ITrackABTesting {
  experiment: string;
  variant: string;
  experimentEvent?: string;
}

export function trackABTesting(params: ITrackABTesting) {
  if (!isDataLayerAvailable()) {
    return;
  }
  window.dataLayer.push({
    event: 'ab-testing',
    ...params,
  });
}

export function trackCustomEvents({
  event,
  value,
}: {
  event: string;
  value: string;
}) {
  if (!isDataLayerAvailable()) return;
  window.dataLayer.push({ event, value });
}

export function trackDynamicEvents({
  event_name,
  step_name,
  group_name,
  value,
  area,
}: {
  event_name: string;
  group_name?: string;
  step_name?: string;
  area?: string;
  value?: string | number;
}) {
  if (!isDataLayerAvailable()) return;

  const payload: Record<string, string | number> = {};
  if (event_name) {
    payload.event_name = event_name;
  }
  if (step_name) {
    payload.step_name = step_name;
  }
  if (group_name) {
    payload.group_name = group_name;
  }
  if (value) {
    payload.value = value;
  }
  if (area) {
    payload.area = area;
  }

  window.dataLayer.push({
    ...payload,
    event: CUSTOM_EVENT_DYNAMIC_EVENT,
  });
}

export function trackForm({ type, params }: FormSubmissionData) {
  if (!isDataLayerAvailable()) return;

  window.dataLayer.push({
    event: type,
    ...params,
  });
}
